<template>
  <div class="video">
    <video :id="vid" class="video-js vjs-fluid" crossorigin playsinline></video>
    <div class="glass-layer" @mouseenter="state.showVideoToolbox = true" @mouseleave="state.showVideoToolbox = false">
      <div
        class="video-toolbox"
        v-if="state.isActiveVjsId && state.showVideoToolbox"
        @click="onVideoToolboxAreaClicked"
      >
        <div class="tool-icon-wrap" @click="enterFullScreen">
          <i class="tool fas fa-search-plus fullscreen"></i>
        </div>
        <i class="tool fa fa-spinner fa-spin" v-if="isDownloadingScreenshot"></i>
        <div class="tool-icon-wrap" @click="takeScreenshot" v-else>
          <i class="tool fas fa-camera dl-image"></i>
          <i class="fa fa-arrow-alt-circle-down mini-arrow"></i>
        </div>
        <i class="tool fa fa-spinner fa-spin" v-if="isDownloadingMovieFile"></i>
        <div class="tool-icon-wrap" @click="tryDownload" v-else>
          <i class="tool fas fa-file-video dl-video"></i>
          <i class="fa fa-arrow-alt-circle-down mini-arrow"></i>
        </div>
      </div>
    </div>
    <div class="video-mask video-mask-black" v-show="!isCurrentMovieReady"></div>
    <div
      class="video-mask video-mask-white"
      v-show="isCurrentMovieReady && !state.isActiveVjsId"
    ></div>
    <div class="post-play-view" v-if="shouldShowPostPlayView">
      <div class="overlay1"></div>
      <div class="overlay2" v-if="isFront">
        <div class="spacer spacer-top"></div>
        <div class="row1">
          <div class="play-continued-btn" @click="playContinuedMovies">
            <i class="fas fa-arrow-right"></i>
            <span class="ib">続きを再生する</span>
          </div>
        </div>
        <div class="spacer spacer-bottom"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMoviePlayerVideo } from 'src/components/lib/MoviePlayer/useMoviePlayerVideo';
import type { MoviePlayerVideoProps, MoviePlayerVideoEmit } from 'src/models/moviePlayer';

const props = withDefaults(defineProps<MoviePlayerVideoProps>(), {
  vid: '',
  currentMovie: null,
  movieFileUrl: null,
  videoControlProperties: () => ({
    playSpeed: 1.0,
    volume: 0,
    // playingだけでは、falseのままでは再実行されないため、
    // 元の動きと異なりますので、playとpause両方でコントロールする。
    play: 0,
    pause: 0,
    tickMsec: 0,
    reset: 0,
    loadMovie: 0, // 動画再生中に複数回切り替える場合があるため、数字で管理する。
    isPlayBackward: false,
  }),
  syncCurrentTime: null,
  titleBarTexts: () => ({ dateDisp: '' }),
});

const emit = defineEmits<MoviePlayerVideoEmit>();

const {
  state,
  shouldShowPostPlayView,
  prepareVideojs,
  onVideoToolboxAreaClicked,
  enterFullScreen,
  takeScreenshot,
  getScreenshotBlob,
  tryDownload,
  playContinuedMovies,
  changeVjsPlayMode,
} = useMoviePlayerVideo(props, emit);

defineExpose({
  prepareVideojs,
  getScreenshotBlob,
  changeVjsPlayMode,
});
</script>

<style lang="scss" scoped>
  .video {
    width: 100%;
    height: 100%;
    position: relative;
    margin: 0 2px;
    flex-grow: 1;
  }
  .video-js {
    width: 100%;
    height: 100%;
    background-color: #ffffff;
    z-index: 25;
  }
  // video.js 7.20.3 の .vjs-fluid:not(.vjs-audio-only-mode) (0,3,0) は詳細度が高くて、
  // .video-js の height:100% を打ち消して height:0（アスペクト比表示）になる。
  // 同等の詳細度で height:100% を再指定し、従来どおりコンテナいっぱいに表示する。
  .video-js.vjs-fluid {
    height: 100%;
  }
  .video-mask {
    width: 100%;
    height: 100%;
    position: absolute;
    z-index: 100;
    top: 0;
    left: 0;
    &.video-mask-black { background-color: #000000; }
    &.video-mask-white { background-color: #ffffff; }
  }
  .glass-layer {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    z-index: 50;
    background-color: transparent;
    .video-toolbox {
      display: flex;
      justify-content: flex-end;
      background-color: rgba(0, 0, 0, 0.5);
      color: #ffffff;
      font-size: 20px;
      height: 28px;
      padding: 0 2px 0 0;
      cursor: pointer;
      .tool-icon-wrap {
        position: relative;
        width: 32px;
        text-align: center;
        .mini-arrow {
          position: absolute;
          right: 0;
          bottom: 2px;
          font-size: 11px;
          border-radius: 8px;
          background-color: #777777;
        }
      }
      .tool {
        margin: 4px 6px;
        &.dl-video { margin-left: 4px; }
      }
    }
  }
  .post-play-view {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    z-index: 100;
    .overlay1, .overlay2 {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
    .overlay1 {
      z-index: 110;
      opacity: 0.6;
      background-color: #000000;
    }
    .overlay2 {
      z-index: 120;
      color: #eeeeee;
      overflow: hidden;
      display: flex;
      flex-direction: column;
      .row1 {
        margin: 0 auto;
        text-align: center;
      }
      .spacer {
        width: 100%;
        &.spacer-top, &.spacer-bottom {
          min-height: 10px;
          flex-grow: 1;
        }
      }
      .play-continued-btn {
        border-radius: 4px;
        border: 1px solid #ffffff;
        margin: 0 auto;
        padding: 2px 24px;
        cursor: pointer;
        font-size: 1.2rem;
      }
  }
}
</style>
